import GoogleGeoCoding from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/Map/GoogleGeoCoding'
import ReverseGeoCodingResult from '@/models/ReverseGeoCodingResult.js'

import LocationGroupOperation from '@/api/PointGroup/LocationGroupOperation/LocationGroupOperation'
import { initialState } from './index'
import { LocationRepository } from '@/models/Location'
import { locationGroupType as groupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'

const actions = {
  SET_PAGE_PERMISSION({ commit }, { pagePermission }) {
    commit('SET_PAGE_PERMISSION', { pagePermission })
  },
  async GET_GROUP_LIST(
    { commit },
    { ownerProfileId, locationGroupType = groupType.ALL },
  ) {
    const res = await LocationGroupOperation.getGroupList(
      ownerProfileId,
      locationGroupType,
    )
    commit('SET_GROUP_LIST', { data: res })
    return res
  },
  async SET_TARGET_GROUP_NOT_POINT({ commit }, { groupData }) {
    commit('RESET_POINT_LIST')
    commit('SET_TARGET_GROUP', { groupData })
  },
  async SET_TARGET_GROUP({ commit, dispatch }, { groupData }) {
    commit('RESET_POINT_LIST')
    commit('SET_TARGET_GROUP', { groupData })
    commit('SET_POINT_LIST_LOADING', true)
    const res = await LocationGroupOperation.getLocationList(
      groupData.id,
      groupData.locationType,
    )
    commit('SET_POINT_LIST_START', { data: res })
    commit('SET_POINT_LIST_LOADING', false)
    dispatch('TRIGGER_FIT_BOUNDS')
    commit('SET_SELECTED_POINT', { data: initialState.editMode.selectedPoint })
  },
  CLEAR_TARGET_GROUP({ commit }) {
    commit('RESET_TARGET_GROUP')
    commit('RESET_POINT_LIST')
  },
  async RESET_TARGET_GROUP({ state, dispatch, commit }) {
    const { id, ownerProfileId } = state.editMode.initialTargetGroup
    await dispatch('GET_GROUP_LIST', { ownerProfileId })
    const targetGroup = state.groupList.find(group => group.id === id)

    commit('RESET_TARGET_GROUP')
    commit('RESET_POINT_LIST')

    dispatch('SET_TARGET_GROUP', { groupData: targetGroup })
  },
  async EDIT_GROUP_NAME({ commit }, { name }) {
    commit('EDIT_GROUP_NAME', { name })
  },
  async EDIT_GROUP_POINT({ commit }, { data }) {
    commit('CHANGE_POINT', { data })
  },
  async SET_CURRENT_POINT_LIST({ commit }, { data }) {
    commit('SET_CURRENT_POINT_LIST', { data })
  },
  async ADD_NEW_POINT({ commit }, { data }) {
    commit('ADD_NEW_POINT', { data })
  },
  async DELETE_NEW_ITEM({ commit }, { data }) {
    commit('DELETE_NEW_ITEM', { data })
  },
  async SET_SELECTED_POINT({ commit }, { data = true }) {
    commit('SET_SELECTED_POINT', { data })
  },
  CLEAR_SELECTED_POINT({ commit }) {
    commit('SET_SELECTED_POINT', { data: initialState.editMode.selectedPoint })
  },
  SET_IS_EDITING_POI_CATEGORY({ commit }) {
    commit('SET_IS_EDITING_POI_CATEGORY', true)
  },
  async SAVE({ dispatch }) {
    await Promise.all([dispatch('savePointList'), dispatch('saveGroup')])
  },
  async savePointList({ getters, commit }) {
    const locationGroup = getters.targetGroup
    const locations = getters.pointListForUI
    try {
      await LocationRepository.saveBulkCustomLocations(
        locationGroup.id,
        locations,
      )
      const savedLocations = await LocationGroupOperation.getLocationList(
        locationGroup.id,
        locationGroup.locationType,
      )
      commit('SET_POINT_LIST_START', { data: savedLocations })
      return Promise.resolve()
    } catch (e) {
      if (Array.isArray(e.locations)) {
        const savedLocations = await LocationGroupOperation.getLocationList(
          locationGroup.id,
        )
        const mergedLocations = [...savedLocations]
        e.locations.forEach(data => {
          const location = data.raw
          if (location.updated || location.toDelete) {
            const index = mergedLocations.findIndex(
              savedLocation => savedLocation.id === location.id,
            )
            mergedLocations[index] = location
          } else if (location.newItem.new) {
            mergedLocations.push(location)
          }
        })
        commit('SET_INITIAL_POINT_LIST', { data: savedLocations })
        commit('SET_CURRENT_POINT_LIST', { data: mergedLocations })
        return Promise.reject(e)
      }
    }
  },
  saveGroup({ getters }) {
    const locationGroup = getters.targetGroup
    if (locationGroup.updated) {
      LocationGroupOperation.editLocationGroup({
        locationGroupId: locationGroup.id,
        name: locationGroup.name,
        accessibleType: locationGroup.accessibleType,
      })
    }
  },
  CHANGE_LOCATION_FILTER({ state }, value) {
    state.locationFilter = value
  },
  SET_LAST_DRAG_END_TIME({ commit }) {
    commit('SET_LAST_DRAG_END_TIME', new Date().getTime())
  },
  INIT({ commit }) {
    commit('INIT', initialState)
  },
  SET_MAP({ commit }, map) {
    commit('SET_MAP', map)
  },
  CLEAR_MAP({ commit }) {
    commit('SET_MAP', null)
  },
  SET_MAP_LOADED({ commit }) {
    commit('SET_MAP_LOADED', true)
  },
  SET_POINT_LIST_IN_FRAME_COUNT({ commit }, count) {
    commit('SET_POINT_LIST_IN_FRAME_COUNT', count)
  },
  async SEARCH({ commit, dispatch }, { query, map }) {
    commit('SET_SEARCH_QUERY', query)
    await dispatch('search', { query, map })
  },
  async search({ commit }, { query }) {
    const components = 'country:jp'
    const options = {
      components,
    }

    const searchAllArea = async () => {
      const res = await GoogleGeoCoding.placeAutoComplete(query, 'ja', options)
      if (res.status === 'INVALID_REQUEST') return Promise.reject()
      else return Promise.resolve(res.predictions)
    }

    try {
      const allAreaResults = await searchAllArea()
      commit('SET_SEARCH_RESULT_LIST', allAreaResults)
    } catch (e) {
      commit('SET_SEARCH_RESULT_LIST', [])
    }
  },
  UPDATE_SEARCH_QUERY({ commit }, keyword) {
    commit('SET_SEARCH_QUERY', keyword)
  },
  CLEAR_SEARCH({ commit }) {
    commit('SET_SEARCH_QUERY', initialState.editMode.search.query)
    commit('SET_SEARCH_RESULT_LIST', initialState.editMode.search.resultList)
    commit(
      'SET_SEARCH_RESULT_INDEX',
      initialState.editMode.search.currentResultIndex,
    )
  },
  FOCUS_OUT_SEARCH_RESULT_LIST({ commit }) {
    commit('SET_SEARCH_RESULT_INDEX', null)
  },
  FOCUS_SEARCH_RESULT_LIST({ commit }) {
    commit('SET_SEARCH_RESULT_INDEX', 0)
  },
  FOCUS_NEXT_SEARCH_RESULST({ state, commit }) {
    const { currentResultIndex, resultList } = state.editMode.search
    if (
      currentResultIndex === null ||
      currentResultIndex === resultList.length - 1
    )
      return
    commit('SET_SEARCH_RESULT_INDEX', currentResultIndex + 1)
  },
  FOCUS_PREV_SEARCH_RESULST({ state, commit }) {
    const { currentResultIndex } = state.editMode.search

    if (currentResultIndex > 0) {
      commit('SET_SEARCH_RESULT_INDEX', currentResultIndex - 1)
    } else {
      commit('SET_SEARCH_RESULT_INDEX', null)
      throw new Error('Prev result was not found')
    }
  },
  async APPLY_SEARCH_RESULT({ state, dispatch }, { result }) {
    const description = result.description
    const { results } = await GoogleGeoCoding.geoCoding(description)
    const [res] = results
    const { geometry } = res
    const { location } = geometry

    const data = {
      id: null,
      name: result.description,
      newItem: {
        id: state.editMode.newItemId,
        new: true,
      },
      shapeType: 'point',
      shape: {
        longitude: location.lng,
        latitude: location.lat,
      },
      status: 'active',
    }

    // リストに追加して選択
    dispatch('ADD_NEW_POINT', { data })
    dispatch('SET_SELECTED_POINT', { data })
  },
  async ADD_NEW_POINT_BY_LAT_LNG(context, { lat, lng }) {
    const { getters, dispatch } = context
    const { results } = await GoogleGeoCoding.reverseGeoCoding({ lat, lng })
    const result = new ReverseGeoCodingResult(results)

    // 地点名をセット（空文字の場合は地点Nとする）
    const description =
      result.pointName.length > 0 ? result.pointName : `地点${this.counter + 1}`

    const newItemId = getters.newItemId

    const data = {
      id: null,
      name: description,
      newItem: {
        id: newItemId,
        new: true,
      },
      shapeType: 'point',
      shape: {
        longitude: lng,
        latitude: lat,
      },
      status: 'active',
    }

    // リストに追加する
    dispatch('ADD_NEW_POINT', {
      data,
    })
    // 選択地点ポイントを変更
    dispatch('SET_SELECTED_POINT', {
      data,
      moveMap: false,
    })
  },
  async APPLY_FOCUSED_SEARCH_RESULT({ state, dispatch }) {
    const { currentResultIndex, resultList } = state.editMode.search
    if (currentResultIndex === null) return
    dispatch('APPLY_SEARCH_RESULT', { result: resultList[currentResultIndex] })
  },
  TRIGGER_FIT_BOUNDS({ commit }) {
    commit('SET_FIT_BOUNDS', true)
    setTimeout(() => {
      commit('SET_FIT_BOUNDS', false)
    }, 1000)
  },
}

export default actions
